import type {
  PortAvailabilityEpisode,
  PortAvailabilityStateKind,
} from "@/features/devices/types";

/**
 * A onda de liga/desliga desenhada na RESOLUÇÃO DO DESENHO, não na do dado.
 *
 * POR QUE ISSO EXISTE. A API entrega episódios por bucket: numa janela de 24 h
 * com bucket de 1 minuto, cada minuto pode render até três episódios
 * (rodando, parada, sem sinal). Em produção isso deu **1213 episódios**, e
 * 1095 deles ocupavam **menos de um pixel** — largura média de 0,49 px.
 *
 * O caminho SVG era montado episódio a episódio, então a onda subia e descia
 * mais de mil vezes ao longo de 592 px. O resultado não era um desenho
 * quadrado legível: era um código de barras verde onde nada se distingue. O
 * dado estava certo — a utilização de 75 % conferia — mas a figura não
 * comunicava nada, e foi lida como "o gráfico não aparece".
 *
 * O CONSERTO. Antes de traçar, os episódios são reduzidos a UMA amostra por
 * coluna de pixel. Cada coluna recebe o estado que ocupou mais tempo dentro
 * dela, e colunas vizinhas de mesmo estado viram um trecho só. A quantidade de
 * degraus passa a ser limitada pela largura do desenho, não pela cadência da
 * telemetria.
 *
 * Isso não inventa nem esconde dado: os totais (rodando/parada/sem sinal) e a
 * porcentagem seguem vindo da API, intactos. O que muda é só a granularidade
 * do traço, que agora não promete mais precisão do que a tela consegue mostrar.
 *
 * É função PURA de propósito. A geometria do gráfico morava dentro do JSX, e
 * jsdom não faz layout — nenhum teste conseguia enxergar que a figura tinha
 * virado ruído. Aqui ela é verificável sem navegador.
 */

/** Uma faixa contígua da onda, já na escala do desenho. */
export interface FaixaDaOnda {
  state: PortAvailabilityStateKind;
  /** Índice da primeira coluna (inclusivo). */
  colunaInicial: number;
  /** Índice da última coluna (inclusivo). */
  colunaFinal: number;
}

/**
 * Reduz os episódios a faixas contíguas, uma amostra por coluna de pixel.
 *
 * `colunas` é quantas colunas o desenho tem de verdade — passar mais do que a
 * largura em pixels só traria de volta o problema que isto resolve.
 */
export function faixasDaOnda(
  episodes: readonly PortAvailabilityEpisode[],
  fromIso: string,
  toIso: string,
  colunas: number,
): FaixaDaOnda[] {
  if (episodes.length === 0 || colunas <= 0) return [];

  const fromMs = Date.parse(fromIso);
  const toMs = Date.parse(toIso);
  // Janela inválida (ou de duração zero) não vira desenho: melhor não desenhar
  // do que desenhar uma figura arbitrária.
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
    return [];
  }
  const span = toMs - fromMs;

  // Quanto tempo cada estado ocupou em cada coluna. O vencedor da coluna é o
  // que ficou mais tempo nela — critério que preserva paradas longas e some
  // com serrilhado de segundos.
  const peso: Map<number, Map<PortAvailabilityStateKind, number>> = new Map();

  for (const episodio of episodes) {
    const inicioMs = Date.parse(episodio.from);
    const fimMs = Date.parse(episodio.to);
    if (!Number.isFinite(inicioMs) || !Number.isFinite(fimMs)) continue;

    // Recorta ao que cabe na janela: episódio que vaza a borda não deve puxar
    // coluna que não existe.
    const inicio = Math.max(inicioMs, fromMs);
    const fim = Math.min(fimMs, toMs);
    if (fim <= inicio) continue;

    const colunaInicial = Math.min(
      colunas - 1,
      Math.floor(((inicio - fromMs) / span) * colunas),
    );
    const colunaFinal = Math.min(
      colunas - 1,
      Math.floor(((fim - fromMs) / span) * colunas),
    );

    for (let coluna = colunaInicial; coluna <= colunaFinal; coluna += 1) {
      // Interseção real entre o episódio e a fatia de tempo desta coluna.
      const colunaInicioMs = fromMs + (coluna / colunas) * span;
      const colunaFimMs = fromMs + ((coluna + 1) / colunas) * span;
      const duracao =
        Math.min(fim, colunaFimMs) - Math.max(inicio, colunaInicioMs);
      if (duracao <= 0) continue;

      let porEstado = peso.get(coluna);
      if (!porEstado) {
        porEstado = new Map();
        peso.set(coluna, porEstado);
      }
      porEstado.set(episodio.state, (porEstado.get(episodio.state) ?? 0) + duracao);
    }
  }

  const faixas: FaixaDaOnda[] = [];
  for (let coluna = 0; coluna < colunas; coluna += 1) {
    const porEstado = peso.get(coluna);
    // Coluna sem nenhum episódio não vira faixa: buraco no desenho é honesto,
    // e inventar um estado ali seria afirmar o que não se mediu.
    if (!porEstado || porEstado.size === 0) continue;

    let vencedor: PortAvailabilityStateKind | null = null;
    let maior = -1;
    for (const [state, duracao] of porEstado) {
      if (duracao > maior) {
        maior = duracao;
        vencedor = state;
      }
    }
    if (!vencedor) continue;

    const ultima = faixas[faixas.length - 1];
    if (ultima && ultima.state === vencedor && ultima.colunaFinal === coluna - 1) {
      ultima.colunaFinal = coluna;
    } else {
      faixas.push({ state: vencedor, colunaInicial: coluna, colunaFinal: coluna });
    }
  }

  return faixas;
}
